/**
 * THE document-type picker — one control for both places a type is chosen:
 * the upload dialog and the document edit form (Library LB6-T10, decision 40
 * / C4; acceptance L6-13).
 *
 * Its options are THE catalogue (lib/use-document-types.ts): the built-in
 * types and the tenant's own custom types, with the labels their rows carry.
 * No type name is written here. A failed read is an error with a retry and a
 * disabled select — never an empty list (the folder picker's rule, LB2-T3).
 * The document's current key stays selectable when the catalogue no longer
 * holds it, so an edit form shows the value the API will accept unchanged.
 */
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DOCUMENT_TYPE_CATALOGUE_ERROR_TEXT,
  documentTypeOptions,
  useDocumentTypes,
  type DocumentTypeScope,
} from '@/lib/use-document-types';

export interface DocumentTypeListErrorProps {
  /** What happens instead, in one sentence. */
  consequence?: string;
  onRetry?: () => void;
}

export function DocumentTypeListError({ consequence, onRetry }: DocumentTypeListErrorProps) {
  return (
    <div role="alert" className="flex flex-wrap items-center gap-x-2 text-xs text-destructive" data-testid="document-type-list-error">
      <span>
        {DOCUMENT_TYPE_CATALOGUE_ERROR_TEXT}
        {consequence ? ` ${consequence}` : ''}
      </span>
      {onRetry ? (
        <Button type="button" variant="link" className="h-auto p-0 text-xs" onClick={() => onRetry()} title="Load the document types again">
          Retry
        </Button>
      ) : null}
    </div>
  );
}

export interface DocumentTypeSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  /** Whose catalogue: the tenant's (default) or the System Library's. */
  scope?: DocumentTypeScope;
  /** Read the catalogue only while the form is shown. */
  enabled?: boolean;
  /** What happens when the catalogue cannot be read (said under the select). */
  errorConsequence?: string;
  triggerClassName?: string;
}

export function DocumentTypeSelect({
  value,
  onValueChange,
  scope = 'tenant',
  enabled = true,
  errorConsequence,
  triggerClassName,
}: DocumentTypeSelectProps) {
  const typesQ = useDocumentTypes({ scope, enabled });
  const failed = typesQ.isError && !typesQ.data;
  const options = documentTypeOptions(typesQ.data ?? [], value);
  return (
    <>
      <Select value={value} onValueChange={onValueChange} disabled={typesQ.isLoading || failed}>
        <SelectTrigger className={triggerClassName} aria-label="Document type">
          <SelectValue placeholder={typesQ.isLoading ? 'Loading types…' : undefined} />
        </SelectTrigger>
        <SelectContent>
          {(typesQ.data ? options : []).map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {failed ? <DocumentTypeListError consequence={errorConsequence} onRetry={() => void typesQ.refetch()} /> : null}
    </>
  );
}
